/**
 * Audit Log CSV Export Utility
 * Exports audit logs to CSV format for download
 */

import { AuditLogEntry, AuditChange } from '../types/audit';
import { formatAuditAction, formatFieldName, formatProjectionChangeValue } from '../services/auditApi';

/**
 * Escape a value for CSV (handle commas, quotes, newlines)
 */
function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Format timestamp for CSV
 */
function formatTimestampForCSV(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/**
 * Format changes array for CSV display
 */
function formatChangesForCSV(changes: AuditChange[]): string {
  if (!changes || changes.length === 0) return '';

  return changes
    .map(change => {
      const field = formatFieldName(change.field);
      const oldVal = formatProjectionChangeValue(change.field, change.oldValue);
      const newVal = formatProjectionChangeValue(change.field, change.newValue);
      return `${field}: ${oldVal} -> ${newVal}`;
    })
    .join('; ');
}

/**
 * Export audit logs to CSV and trigger download
 */
export function exportAuditLogsToCSV(
  logs: AuditLogEntry[],
  filename?: string
): void {
  if (logs.length === 0) return;

  // CSV headers
  const headers = [
    'Timestamp',
    'User Name',
    'User Email',
    'Action',
    'Location',
    'Details',
    'IP Address',
  ];

  // Build CSV rows
  const rows = logs.map(entry => {
    const timestamp = formatTimestampForCSV(entry.timestamp);
    const userName = entry.userDisplayName;
    const userEmail = entry.userEmail;
    const action = formatAuditAction(entry.action);
    const location = entry.locationName || '';
    const details = entry.details?.changes
      ? formatChangesForCSV(entry.details.changes)
      : '';
    const ipAddress = entry.details?.ipAddress || '';

    return [
      escapeCSV(timestamp),
      escapeCSV(userName),
      escapeCSV(userEmail),
      escapeCSV(action),
      escapeCSV(location),
      escapeCSV(details),
      escapeCSV(ipAddress),
    ].join(',');
  });

  // Combine headers and rows
  const csvContent = [headers.join(','), ...rows].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  // Generate filename with date
  const today = new Date().toISOString().split('T')[0];
  const downloadFilename = filename || `audit-log-${today}.csv`;

  // Create download link and click it
  const link = document.createElement('a');
  link.href = url;
  link.download = downloadFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Cleanup
  URL.revokeObjectURL(url);
}
